import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './DestinationsMap.css';
import { images } from '../../shared/assets';

interface MapState {
  hoveredLocation: 'atacama' | 'patagonia' | 'iguazu' | 'santa-catarina' | null;
  showAirports: boolean;
  showFlights: boolean;
}

const DestinationsMap: React.FC = () => {
  const { t } = useTranslation();
  const [mapState, setMapState] = useState<MapState>({
    hoveredLocation: null,
    showAirports: false,
    showFlights: false
  });

  // Debounce timer for hover events
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleLocationHover = useCallback((location: MapState['hoveredLocation']) => {
    // Only allow hover when toggles are off
    if (mapState.showAirports || mapState.showFlights) return;
    
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Set a new timeout for debounced hover
    hoverTimeoutRef.current = setTimeout(() => {
      setMapState(prev => ({ ...prev, hoveredLocation: location }));
    }, location === null ? 150 : 50); // Faster on enter, slower on leave
  }, [mapState.showAirports, mapState.showFlights]);

  const handleToggleChange = (toggle: 'airports' | 'flights') => {
    if (toggle === 'airports') {
      setMapState(prev => ({ 
        ...prev, 
        showAirports: !prev.showAirports,
        hoveredLocation: null // Clear hover when toggling
      }));
    } else {
      setMapState(prev => ({ 
        ...prev, 
        showFlights: !prev.showFlights,
        hoveredLocation: null // Clear hover when toggling
      }));
    }
  };

  // Dynamic image sets based on hover state
  const getCollageImages = () => {
    const mapAssets = images.destinationsPage.figmaAssets.mapSection;
    switch (mapState.hoveredLocation) {
      case 'atacama':
        return {
          image1: mapAssets.atacamaCollage1,
          image2: mapAssets.atacamaCollage2,
          image3: mapAssets.atacamaCollage3
        };
      case 'iguazu':
        return {
          image1: mapAssets.iguazuCollage1,
          image2: mapAssets.iguazuCollage2,
          image3: mapAssets.iguazuCollage3
        };
      case 'santa-catarina':
        return {
          image1: mapAssets.santaCatarinaCollage1,
          image2: mapAssets.santaCatarinaCollage2,
          image3: mapAssets.santaCatarinaCollage3
        };
      case 'patagonia':
        return {
          image1: mapAssets.patagoniaCollage1,
          image2: mapAssets.patagoniaCollage2,
          image3: mapAssets.patagoniaCollage3
        };
      default:
        return {
          image1: mapAssets.defaultCollage1,
          image2: mapAssets.defaultCollage2,
          image3: mapAssets.defaultCollage3
        };
    }
  };

  const getMapSVG = () => {
    const mapAssets = images.destinationsPage.figmaAssets.mapSection;
    if (!mapState.hoveredLocation) {
      return mapAssets.defaultMapSvg;
    }
    
    switch (mapState.hoveredLocation) {
      case 'atacama':
        return mapAssets.mapAtacama;
      case 'iguazu':
        return mapAssets.mapIguazu;
      case 'santa-catarina':
        return mapAssets.mapSantaCatarina;
      case 'patagonia':
        return mapAssets.mapPatagonia;
      default:
        return mapAssets.mapDefault;
    }
  };

  const collageImages = getCollageImages();

  return (
    <div className="destinations-map-wrapper">
      {/* Text Section */}
      <div className="destinations-text-section" data-name="Body Text below Header" id="node-3102_37099">
        <div className="destinations-text-content">
          <p>
            {t('pages/destinations:intro.text')}
          </p>
        </div>
      </div>

      {/* Map Section */}
      <div className="map-section" data-name="Collage + Map Block" id="node-3102_37098">
        {/* Left side content - appears when toggles are active */}
        {(mapState.showAirports || mapState.showFlights) && (
          <div className="map-left-content">
            <h2>{t('pages/destinations:map.combineTitle')}</h2>
            <p>{t('pages/destinations:map.combineDescription')}</p>
            <button className="plan-trip-button">
              <span>{t('pages/destinations:map.planTrip')}</span>
            </button>
          </div>
        )}

        {/* Collage Images - only show when no toggles are active */}
        {!mapState.showAirports && !mapState.showFlights && (
          <>
            <div 
              className="collage-image collage-image-1"
              style={{ 
                backgroundImage: `url('${collageImages.image1}')`,
                animationDelay: '0.1s'
              }}
            />
            <div 
              className="collage-image collage-image-2"
              style={{ 
                backgroundImage: `url('${collageImages.image2}')`,
                animationDelay: '0.2s'
              }}
            />
            <div 
              className="collage-image collage-image-3"
              style={{ 
                backgroundImage: `url('${collageImages.image3}')`,
                animationDelay: '0.3s'
              }}
            />
          </>
        )}

        {/* Map SVG - Absolutely positioned */}
        <img 
          src={getMapSVG()} 
          alt="South America Map"
          className="south-america-map"
          key={mapState.hoveredLocation || 'default'}
        />
        
        {/* Airport markers - only show when airports toggle is on */}
        {mapState.showAirports && (
          <div className="airport-markers">
                  <div className="airport-marker" style={{left: '0px', top: '490px', '--marker-index': 0} as React.CSSProperties}>
                    <span>Santiago</span>
                    <div className="airport-dot" />
                  </div>
                  <div className="airport-marker" style={{left: '188px', top: '503px', '--marker-index': 1} as React.CSSProperties}>
                    <div className="airport-dot" />
                    <span>Buenos aires</span>
                  </div>
                  <div className="airport-marker" style={{left: '321px', top: '357px', '--marker-index': 2} as React.CSSProperties}>
                    <div className="airport-dot" />
                    <span>rio de janeiro</span>
                  </div>
                  <div className="airport-marker" style={{left: '288px', top: '389px', '--marker-index': 3} as React.CSSProperties}>
                    <div className="airport-dot" />
                    <span>san paolo</span>
                  </div>
                  <div className="airport-marker" style={{left: '111px', top: '720px', '--marker-index': 4} as React.CSSProperties}>
                    <div className="airport-dot" />
                    <span>Punta Arenas</span>
                  </div>
                  <div className="airport-marker" style={{left: '117px', top: '689px', '--marker-index': 5} as React.CSSProperties}>
                    <div className="airport-dot" />
                    <span>el calafate</span>
                  </div>
                  <div className="airport-marker" style={{left: '-18px', top: '670px', '--marker-index': 6} as React.CSSProperties}>
                    <span>puerto natales</span>
                  </div>
                  <div className="airport-marker" style={{left: '80px', top: '690px', '--marker-index': 7} as React.CSSProperties}>
                    <div className="airport-dot" />
                  </div>
                  <div className="airport-marker-simple" style={{left: '185px', top: '354px', '--marker-index': 8} as React.CSSProperties}>
                    <span>foz</span>
                  </div>
                  <div className="airport-marker-simple" style={{left: '113px', top: '440px', '--marker-index': 9} as React.CSSProperties}>
                    <span>puerto iguazu</span>
                  </div>
            <div className="airport-marker-simple" style={{left: '105px', top: '404px', '--marker-index': 10} as React.CSSProperties}>
              <span>calama</span>
            </div>
          </div>
        )}

        {/* Flight connections - only show when flights toggle is on */}
        {mapState.showFlights && (
          <img 
            src={images.destinationsPage.figmaAssets.mapSection.flightConnections} 
            alt="Flight connections"
            className="flight-connections"
          />
        )}
        
        {/* Location Popups */}
        <div className="map-locations" data-name="Popups" id="node-3202_37997">
                {/* Atacama */}
                <div 
                  className="map-location atacama-location" 
                  data-name="Atacama" 
                  id="node-3202_37912"
                  onMouseEnter={() => handleLocationHover('atacama')}
                  onMouseLeave={() => handleLocationHover(null)}
                >
                  <span>Atacama</span>
                  <img src={images.destinationsPage.figmaAssets.mapSection.pointerRight} alt="Pointer" className="location-pointer" />
                </div>

                {/* Patagonia */}
                <div 
                  className="map-location patagonia-location" 
                  data-name="Patagonia" 
                  id="node-3202_37913"
                  onMouseEnter={() => handleLocationHover('patagonia')}
                  onMouseLeave={() => handleLocationHover(null)}
                >
                  <span>Patagonia</span>
                  <img src={images.destinationsPage.figmaAssets.mapSection.pointerRight} alt="Pointer" className="location-pointer" />
                </div>

                {/* Iguazu */}
                <div 
                  className="map-location iguazu-location" 
                  data-name="iguazu" 
                  id="node-3202_37915"
                  onMouseEnter={() => handleLocationHover('iguazu')}
                  onMouseLeave={() => handleLocationHover(null)}
                >
                  <span>Iguazu</span>
                  <img src={images.destinationsPage.figmaAssets.mapSection.pointerDown} alt="Pointer" className="location-pointer-vertical" />
                </div>

                {/* Santa Catarina */}
                <div 
                  className="map-location santa-catarina-location" 
                  data-name="Santa Catarina" 
                  id="node-3202_37914"
                  onMouseEnter={() => handleLocationHover('santa-catarina')}
                  onMouseLeave={() => handleLocationHover(null)}
                >
                  <img src={images.destinationsPage.figmaAssets.mapSection.pointerLeft} alt="Pointer" className="location-pointer-reversed" />
                  <span>Santa Catarina</span>
          </div>
        </div>

        {/* Toggle Switches */}
        <div className="map-toggles" data-name="Switches" id="node-3202_37995">
                <button 
                  className={`toggle-item ${mapState.showAirports ? 'active' : ''}`} 
                  data-name="Switch 1" 
                  id="node-3202_37968"
                  onClick={() => handleToggleChange('airports')}
                >
                  <img src={mapState.showAirports ? images.destinationsPage.figmaAssets.mapSection.toggleOn : images.destinationsPage.figmaAssets.mapSection.toggleOff} alt="Toggle Switch" />
                  <span>{t('pages/destinations:map.toggles.airports')}</span>
                </button>
                <button 
                  className={`toggle-item ${mapState.showFlights ? 'active' : ''}`} 
                  data-name="Switch 2" 
                  id="node-3202_37969"
                  onClick={() => handleToggleChange('flights')}
                >
                  <img src={mapState.showFlights ? images.destinationsPage.figmaAssets.mapSection.toggleOn : images.destinationsPage.figmaAssets.mapSection.toggleOff} alt="Toggle Switch" />
            <span>{t('pages/destinations:map.toggles.flights')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DestinationsMap;